import { useState } from "react";

export default function Applications() {
  const [activeCategory, setActiveCategory] = useState("all");

  // Categories and their peptides
  const categories = {
    all: { name: "All Categories", color: "#3498DB" },
    metabolic: { name: "Metabolic", color: "#2ECC71" },
    immunity: { name: "Immunity", color: "#9B59B6" },
    cardiovascular: { name: "Cardiovascular", color: "#E74C3C" },
    neurology: { name: "Neurology", color: "#F39C12" },
    other: { name: "Other", color: "#34495E" }
  };

  // Peptide data
  const peptides = [
    { 
      id: 1, 
      name: "Insulin & Analogs", 
      description: "Cornerstone of diabetes treatment, regulating blood glucose",
      examples: "Human insulin, insulin glargine, insulin lispro",
      category: "metabolic"
    },
    { 
      id: 2, 
      name: "GLP-1 Receptor Agonists", 
      description: "Manage diabetes and obesity by regulating appetite and glucose",
      examples: "Semaglutide (Ozempic®, Wegovy®), liraglutide, tirzepatide (Mounjaro®)",
      category: "metabolic"
    },
    { 
      id: 3, 
      name: "Thymosin Peptides", 
      description: "Modulate immune function and tissue repair",
      examples: "Thymosin alpha-1, thymosin beta-4",
      category: "immunity"
    },
    { 
      id: 4, 
      name: "Natriuretic Peptides", 
      description: "Regulate blood pressure, fluid balance, and cardiovascular function",
      examples: "Nesiritide, carperitide",
      category: "cardiovascular"
    },
    { 
      id: 5, 
      name: "Oxytocin", 
      description: "Beyond obstetrics, being investigated for psychiatric applications",
      examples: "Research in autism spectrum disorders, social anxiety, depression",
      category: "neurology"
    }
  ];

  // Filter peptides based on active category
  const filteredPeptides = activeCategory === "all" 
    ? peptides 
    : peptides.filter(peptide => peptide.category === activeCategory);

  return (
    <section id="applications" className="mb-16 scroll-mt-20">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
        Categorized Summary of Key Peptides by Application
      </h2>
      
      <p className="text-lg mb-8">
        Peptides play diverse roles across medical specialties. Here's a categorized 
        overview of significant peptide therapies and their applications.
      </p>
      
      {/* Application Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {/* Metabolic & Endocrine */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-[#3498DB] text-white p-4">
            <h3 className="font-serif text-xl font-semibold">Metabolic & Endocrine</h3>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              <li>
                <h4 className="font-bold text-[#2C3E50]">Insulin & Analogs</h4>
                <p className="text-sm mb-1">Cornerstone of diabetes treatment, regulating blood glucose</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Human insulin, insulin glargine, insulin lispro</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">GLP-1 Receptor Agonists</h4>
                <p className="text-sm mb-1">Manage diabetes and obesity by regulating appetite and glucose</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Semaglutide (Ozempic®, Wegovy®), liraglutide, tirzepatide (Mounjaro®)</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Growth Hormone & Analogs</h4>
                <p className="text-sm mb-1">Treat growth hormone deficiency and related conditions</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Somatropin, CJC-1295, ipamorelin</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">ACTH & Analogs</h4>
                <p className="text-sm mb-1">Stimulate adrenal cortex; used for endocrine testing and therapies</p>
                <p className="text-xs text-[#7F8C8D]">Example: Corticotropin, tetracosactide</p>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Immunity & Inflammation */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-[#3498DB] text-white p-4">
            <h3 className="font-serif text-xl font-semibold">Immunity & Inflammation</h3>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              <li>
                <h4 className="font-bold text-[#2C3E50]">Thymosin Peptides</h4>
                <p className="text-sm mb-1">Modulate immune function and tissue repair</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Thymosin alpha-1, thymosin beta-4</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">BPC-157</h4>
                <p className="text-sm mb-1">Investigational peptide with potential tissue-healing properties</p>
                <p className="text-xs text-[#7F8C8D]">Research focus: Gut healing, soft tissue repair, neuroprotection</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Antimicrobial Peptides</h4>
                <p className="text-sm mb-1">Natural antibiotics that combat bacteria, fungi, and viruses</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Defensins, cathelicidins, colistin</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Immunomodulatory Peptides</h4>
                <p className="text-sm mb-1">Balance immune responses in autoimmune and inflammatory conditions</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Glatiramer acetate, integrin inhibitors</p>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Cardiovascular & Vascular */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-[#3498DB] text-white p-4">
            <h3 className="font-serif text-xl font-semibold">Cardiovascular & Vascular</h3>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              <li>
                <h4 className="font-bold text-[#2C3E50]">Natriuretic Peptides</h4>
                <p className="text-sm mb-1">Regulate blood pressure, fluid balance, and cardiovascular function</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Nesiritide, carperitide</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Angiotensin Peptides</h4>
                <p className="text-sm mb-1">Target the renin-angiotensin system for blood pressure control</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Angiotensin II (Giapreza®)</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Vasopressin Analogs</h4>
                <p className="text-sm mb-1">Manage blood pressure, particularly in shock states</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Vasopressin, terlipressin</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Endothelin Receptor Antagonists</h4>
                <p className="text-sm mb-1">Treat pulmonary arterial hypertension</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Various peptide and non-peptide antagonists</p>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Neurology & Psychiatry */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-[#3498DB] text-white p-4">
            <h3 className="font-serif text-xl font-semibold">Neurology & Psychiatry</h3>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              <li>
                <h4 className="font-bold text-[#2C3E50]">Oxytocin</h4>
                <p className="text-sm mb-1">Beyond obstetrics, being investigated for psychiatric applications</p>
                <p className="text-xs text-[#7F8C8D]">Research focus: Autism spectrum disorders, social anxiety, depression</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Semax & Selank</h4>
                <p className="text-sm mb-1">Nootropic and anxiolytic peptides used in some countries</p>
                <p className="text-xs text-[#7F8C8D]">Applications: Cognitive enhancement, anxiety management</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Neuropeptide Modulators</h4>
                <p className="text-sm mb-1">Target specific neurological pathways</p>
                <p className="text-xs text-[#7F8C8D]">Examples: Substance P antagonists, CGRP antagonists for migraine</p>
              </li>
              <li>
                <h4 className="font-bold text-[#2C3E50]">Cerebrolysin</h4>
                <p className="text-sm mb-1">Mixture of neuropeptides used for stroke and dementia in some regions</p>
                <p className="text-xs text-[#7F8C8D]">Research focus: Neuroprotection, neuroregeneration</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Interactive Explorer */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">Interactive Peptide Explorer</h3>
        </div>
        <div className="p-6">
          <p className="mb-6 text-center">Select a category to explore specific peptides and their applications</p>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {Object.entries(categories).map(([key, category]) => (
              <button
                key={key}
                className={`px-4 py-2 rounded-full font-semibold transition-colors ${
                  activeCategory === key
                    ? `bg-[${category.color}] text-white`
                    : `bg-[#EBF5FB] text-[#2C3E50] hover:bg-[${category.color}] hover:text-white`
                }`}
                style={{ 
                  backgroundColor: activeCategory === key ? category.color : '#EBF5FB',
                  color: activeCategory === key ? 'white' : '#2C3E50'
                }}
                onClick={() => setActiveCategory(key)}
              >
                {category.name}
              </button>
            ))}
          </div>
          
          <div className="peptide-explorer-content">
            {filteredPeptides.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredPeptides.map(peptide => (
                  <div key={peptide.id} className="bg-[#EBF5FB] p-4 rounded-lg">
                    <h4 className="font-bold text-[#2C3E50] mb-2">{peptide.name}</h4>
                    <p className="text-sm mb-2">{peptide.description}</p>
                    <p className="text-xs text-[#7F8C8D]"><strong>Examples:</strong> {peptide.examples}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-[#7F8C8D] italic">
                <p>No peptides found in this category.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-[#ECF0F1] p-6 rounded-lg">
        <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">The Future of Peptide Applications</h3>
        <p className="mb-4">Research into peptide therapeutics continues to expand into new frontiers:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-bold text-[#2C3E50] mb-2">Longevity & Aging</h4>
            <p className="text-sm">
              Peptides being studied for their potential to slow aging processes and extend healthy lifespan
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-bold text-[#2C3E50] mb-2">Targeted Cancer Therapy</h4>
            <p className="text-sm">
              Peptide-drug conjugates delivering toxic payloads directly to cancer cells while sparing healthy tissue
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-bold text-[#2C3E50] mb-2">Biomarker Detection</h4>
            <p className="text-sm">
              Diagnostic peptides designed to bind to specific disease markers for early detection and monitoring
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
